/**
 * ⚠️ FUENTE NO AUTORITATIVA — SOLO PREVIEW EN UI
 *
 * Las RPCs de Postgres son la ÚNICA fuente de verdad para cálculos financieros:
 *   - public.compute_purchase_fees(...)
 *   - public.compute_resale_fees(...)
 *   - public.get_max_resale_price(...)
 *
 * Este archivo replica las fórmulas para mostrar previews instantáneas en la UI
 * sin round-trip a la DB. NUNCA usar estos valores para cobrar, acreditar wallet,
 * crear settlements ni decidir flujos. El edge `payments-webhook` y los checkouts
 * llaman las RPCs antes de mover dinero. Cualquier divergencia entre TS y RPC
 * debe resolverse modificando primero la RPC; este archivo se ajusta después.
 *
 * Si cambia `platform_settings`, llamar `invalidateFeeConfig()` para refrescar.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PlatformFeeConfig {
  // Compra primaria
  service_fee_percent: number;
  service_fee_fixed: number;
  // Reventa
  resale_platform_fee_percent: number;
  resale_payment_fee_percent: number;
  resale_payment_fee_fixed: number;
  max_resale_markup_percent: number; // ej: 110 = +10% sobre base
  // Moneda
  currency: string;
}

const DEFAULTS: PlatformFeeConfig = {
  service_fee_percent: 10,
  service_fee_fixed: 500,
  resale_platform_fee_percent: 7,
  resale_payment_fee_percent: 3,
  resale_payment_fee_fixed: 0,
  max_resale_markup_percent: 110,
  currency: "CRC",
};

let cache: PlatformFeeConfig | null = null;
let inflight: Promise<PlatformFeeConfig> | null = null;

async function loadConfig(): Promise<PlatformFeeConfig> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await supabase
        .from("platform_settings")
        .select(
          "service_fee_percent, service_fee_fixed, resale_platform_fee_percent, resale_payment_fee_percent, resale_payment_fee_fixed, max_resale_markup_percent, currency"
        )
        .limit(1)
        .single();
      if (data) {
        cache = {
          service_fee_percent: Number(data.service_fee_percent) || 0,
          service_fee_fixed: Number(data.service_fee_fixed) || 0,
          resale_platform_fee_percent: Number(data.resale_platform_fee_percent) || 0,
          resale_payment_fee_percent: Number(data.resale_payment_fee_percent) || 0,
          resale_payment_fee_fixed: Number(data.resale_payment_fee_fixed) || 0,
          max_resale_markup_percent:
            Number(data.max_resale_markup_percent) || DEFAULTS.max_resale_markup_percent,
          currency: (data.currency as string) || "CRC",
        };
        return cache;
      }
    } catch (e) {
      console.warn("[fees] using defaults:", e);
    }
    cache = DEFAULTS;
    return cache;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function invalidateFeeConfig() {
  cache = null;
}

export function usePlatformFeeConfig(): PlatformFeeConfig {
  const [cfg, setCfg] = useState<PlatformFeeConfig>(cache ?? DEFAULTS);
  useEffect(() => {
    let mounted = true;
    loadConfig().then((c) => {
      if (mounted) setCfg(c);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return cfg;
}

/** Service fee for a single ticket given its base unit price (which may include organizer fee). */
export function computeServiceFee(unitPrice: number, cfg: PlatformFeeConfig): number {
  const percentPart = (Number(unitPrice) * Number(cfg.service_fee_percent)) / 100;
  return Math.round(percentPart + Number(cfg.service_fee_fixed));
}

/** Total que paga el comprador en compra primaria por una unidad. */
export function computeTotalPaid(basePrice: number, cfg: PlatformFeeConfig): number {
  return Math.round(basePrice + computeServiceFee(basePrice, cfg));
}

/**
 * 🪙 ALL-IN PRICING — Único helper autorizado para mostrar precios en listings/UI.
 *
 * Devuelve el precio FINAL que el usuario verá y pagará (base + organizer fee
 * ya incluido en `basePrice` + service fee de plataforma). Esto es solo para
 * presentación: el cobro real lo decide el backend (RPC `compute_purchase_fees`).
 *
 * Usar en: cards de evento, mapa de venue, selector de zonas, listas de tipos
 * de ticket, hover/tooltips. NO usar para cálculos contables.
 */
export function computeDisplayPrice(basePrice: number, cfg: PlatformFeeConfig): number {
  return computeTotalPaid(basePrice, cfg);
}

/** Precio máximo permitido en reventa según markup configurado. */
export function computeMaxResalePrice(basePrice: number, cfg: PlatformFeeConfig): number {
  return Math.round((Number(basePrice) * Number(cfg.max_resale_markup_percent)) / 100);
}

export interface ResaleBreakdown {
  resalePrice: number;
  platformFee: number;
  paymentFee: number;
  totalFees: number;
  sellerNet: number;
  totalPaid: number;
  estimatedProfit: number;
}

/** Desglose financiero de reventa. estimatedProfit = sellerNet - totalPaid. */
export function computeResaleBreakdown(
  basePrice: number,
  resalePrice: number,
  cfg: PlatformFeeConfig
): ResaleBreakdown {
  const platformFee = Math.round((resalePrice * cfg.resale_platform_fee_percent) / 100);
  const paymentFee =
    Math.round((resalePrice * cfg.resale_payment_fee_percent) / 100) +
    Number(cfg.resale_payment_fee_fixed || 0);
  const totalFees = platformFee + paymentFee;
  const sellerNet = Math.max(0, resalePrice - totalFees);
  const totalPaid = computeTotalPaid(basePrice, cfg);
  const estimatedProfit = sellerNet - totalPaid;
  return { resalePrice, platformFee, paymentFee, totalFees, sellerNet, totalPaid, estimatedProfit };
}
